import React, { useState, lazy, Suspense, useCallback } from 'react'
import {
    Box,
    CircularProgress,
    makeStyles,
    Tab,
    Tabs,
    Theme,
    Typography,
} from '@material-ui/core'

// Lazy load tab components for faster initial render
const SampleW9s = lazy(() =>
    import('./SampleW9s').then((m) => ({ default: m.SampleW9s })),
)
const VendorW9s = lazy(() =>
    import('./VendorW9s').then((m) => ({ default: m.VendorW9s })),
)

type W9TabType = 'sample' | 'vendor'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    title: {
        fontWeight: 600,
        letterSpacing: '-0.02em',
    },
    tabsContainer: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        marginBottom: theme.spacing(2),
    },
    tab: {
        textTransform: 'none',
        minWidth: 120,
        fontWeight: 500,
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
    },
}))

// Loading fallback component
const TabLoading: React.FC = () => {
    const classes = useStyles()
    return (
        <Box className={classes.loadingContainer}>
            <CircularProgress size={32} />
        </Box>
    )
}

export const W9Manager: React.FC = () => {
    const classes = useStyles()
    const [activeTab, setActiveTab] = useState<W9TabType>('sample')

    const handleTabChange = useCallback(
        (_event: React.ChangeEvent<{}>, newValue: W9TabType) => {
            setActiveTab(newValue)
        },
        [],
    )

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Typography
                    variant="h5"
                    component="h2"
                    className={classes.title}
                >
                    W-9 Manager
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Manage sample and vendor W-9 forms.
                </Typography>
            </Box>

            <Box className={classes.tabsContainer}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab
                        label="Sample W-9s"
                        value="sample"
                        className={classes.tab}
                    />
                    <Tab
                        label="Vendor W-9s"
                        value="vendor"
                        className={classes.tab}
                    />
                </Tabs>
            </Box>

            <Suspense fallback={<TabLoading />}>
                {activeTab === 'sample' && <SampleW9s />}
                {activeTab === 'vendor' && <VendorW9s />}
            </Suspense>
        </Box>
    )
}
